/**
 * UI copy in one place (English for now; ready to be swapped for an i18n
 * catalogue). Content from the knowledge base stays in its own language.
 */

export const t = {
  app: {
    name: "Smake",
    tagline: "Company knowledge, tools and safe changes",
    skip: "Skip to content",
  },
  nav: {
    home: "Home",
    ask: "Ask",
    schorschi: "Schorschi",
    knowledge: "Knowledge",
    search: "Search",
    overview: "Overview",
    graphViews: "Graph views",
    inbox: "Inbox",
    changeLog: "Change log",
    views: "Views",
    sources: "Sources",
    maintenance: "Maintenance",
    tools: "Tools",
    actions: "Actions",
    jobs: "Tasks & Jobs",
    approvals: "Approvals",
    changes: "Changes",
    connections: "Connections",
    connectedApps: "Connected apps",
    activity: "My activity",
    shares: "Shares & links",
    files: "Files",
    webhooks: "Webhooks",
    remote: "Remote sessions",
    audit: "Audit",
    admin: "Admin",
    access: "Access",
    users: "Users",
    datasources: "Datasources",
    connectionProviders: "Connection providers",
    mcpClients: "MCP clients",
    ai: "AI",
    adminRunbooks: "Action settings",
    knowledgeSources: "Knowledge sources",
    settings: "Settings",
    system: "System",
    feedback: "Feedback",
    sectionWork: "Work",
    sectionYou: "You",
    sectionAdmin: "Administration",
  },
  auth: {
    signIn: "Sign in",
    signOut: "Sign out",
    signInTitle: "Sign in to Smake",
    signInBody: "Use your Smake account. You'll come right back here.",
    signedOutTitle: "You're signed out",
    signedOutBody: "Sign in again whenever you're ready.",
    redirecting: "Taking you to sign-in…",
    expiredTitle: "Your session has ended",
    expiredBody: "Sign in again to continue. Unsaved input on this page may be lost.",
    disabled: "Your account is disabled. Contact an admin.",
  },
  common: {
    loading: "Loading…",
    retry: "Try again",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    remove: "Remove",
    edit: "Edit",
    create: "Create",
    add: "Add",
    confirm: "Confirm",
    copy: "Copy",
    copied: "Copied",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    all: "All",
    none: "None",
    yes: "Yes",
    no: "No",
    back: "Back",
    open: "Open",
    details: "Details",
    reason: "Reason",
    reasonHint: "Why? This goes into the audit trail.",
    optional: "optional",
    required: "required",
    unknown: "Unknown",
    never: "never",
    showMore: "Show more",
    showLess: "Show less",
    loadMore: "Load more",
    refresh: "Refresh",
    viewAll: "View all",
    test: "Test",
    enabled: "Enabled",
    disabled: "Disabled",
    run: "Run",
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    menu: "Menu",
    noResults: "No results",
  },
  errors: {
    generic: "Something went wrong",
    network: "The server could not be reached. Check your connection and try again.",
    forbidden: "You don't have access to this.",
    notFound: "We couldn't find that.",
    notAvailableTitle: "Not available yet",
    notAvailableBody: "This part of Smake is still being built. It will light up here automatically once the server supports it.",
    aiUnavailable: "AI answers are switched off right now. Try the knowledge search instead.",
    pageNotFound: "Page not found",
    pageNotFoundBody: "The link may be old, or the page moved.",
    noAccessTitle: "You don't have access",
    noAccessBody: "This area needs a permission you don't have. Ask a Smake admin if you need it.",
  },
  home: {
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    intro: "What needs you, what happened and what you can do.",
    whatYouCanDo: "What you can do",
    whatYouCanDoEmpty: "You don't have any tools yet. Ask an admin to grant you a role.",
    tools: (n: number) => `${n} tool${n === 1 ? "" : "s"}`,
    connections: "Connections",
    connectionsAllGood: "All your accounts are connected.",
    pendingApprovals: "Waiting for approval",
    pendingApprovalsEmpty: "Nothing waiting for you.",
    recentJobs: "Recent jobs",
    recentJobsEmpty: "No jobs yet. Long-running tools and syncs show up here.",
    recentActivity: "Your recent activity",
    recentActivityEmpty: "Nothing yet. What you do through Claude or this app shows up here.",
    claudeTitle: "Use it in Claude",
    claudeCodeLabel: "Claude Code",
    claudeCodeHint: "Run these two commands once in a terminal (needs read access to the GitHub repo smakecloud/claude-plugins; in the desktop app, add smake from your organisation's plugins instead):",
    claudeAiLabel: "claude.ai",
    // Until the cutover the org's "Smake" connector still points at v1 (docs/v2/cutover.md §12 flips this text).
    claudeAiHint: "Until the switch, the organisation's Smake connector still points at the old server — add a custom connector with this URL (Settings → Connectors):",
    claudeOneOf: "Use the plugin or a claude.ai connector in Claude Code, not both — otherwise every tool appears twice.",
    getStarted: "Get started",
    getStartedIntro: "Three steps to set smake-mcp up for what you are allowed to do.",
    getStartedDismiss: "Hide",
    getStartedAccounts: "Connect your accounts",
    getStartedAccountsTodo: (n: number) => `${n} account${n === 1 ? "" : "s"} still to connect — changes run as you.`,
    getStartedAccountsDone: "All your accounts are connected.",
    getStartedClaude: "Add smake to Claude",
    getStartedClaudeHint: "The Claude Code plugin or the claude.ai connector — see “Use it in Claude”.",
    getStartedClaudeDone: (n: number) => `${n} app${n === 1 ? "" : "s"} connected.`,
    getStartedOnboard: "Let Claude set you up",
    getStartedOnboardHint:
      "A personal checklist from your access: accounts, targets, what you can do, how your changes are approved. In Claude Code run the skill below; in claude.ai pick “Set up smake-mcp for me” under + → Smake.",
    aiBudget: "AI budget",
    aiBudgetUsage: "Usage",
    aiBudgetLeft: "left",
    aiBudgetResets: (when: string) => `Tightest limit resets ${when} (UTC periods).`,
    aiBudgetNone: "No AI budget limits apply to you.",
    aiBudgetLow: "running low",
    aiBudgetUsedUp: "used up",
    aiBudgetTopups: (list: string) => `Top-ups: ${list}.`,
    // Command center
    statusLabel: "At a glance",
    statApprovals: "Approvals",
    statApprovalsWaiting: "waiting for you",
    statApprovalsNone: "nothing waiting",
    statInbox: "Messages",
    statInboxNone: "all read",
    statTodos: "Your todos",
    statTodosHint: (urgent: number) => (urgent ? `${urgent} urgent or high` : "open, none urgent"),
    statTodosNone: "nothing open",
    statJobs: "Running jobs",
    statJobsRunning: "working now",
    statJobsNone: "nothing running",
    statKnowledge: "Knowledge",
    statKnowledgeWaiting: "in the inbox to decide",
    statKnowledgeNone: "inbox all decided",
    statBudget: "AI budget left",
    statBudgetHint: (limit: string, resets: string) => `of ${limit} · resets ${resets}`,
    statBudgetNone: "no limits apply to you",
    needsYou: "Needs you",
    needsYouClear: "Nothing needs you right now",
    needsYouClearHint: "Changes to approve, messages, your todos and knowledge decisions show up here.",
    sectionMessages: "Unread messages",
    sectionTodos: "Your todos",
    sectionKnowledge: "Knowledge inbox",
    todoPersonal: "personal",
    todoShared: "shared",
    knowledgeNoise: (n: string) => `${n} likely noise: start there and reject them in groups`,
    knowledgeWaiting: (n: string) => `All ${n} waiting for a decision`,
    andMore: (n: number) => `and ${n} more`,
    nightShift: "Night shift",
    nightShiftOpen: "Todos",
    nightShiftOn: "on",
    nightShiftOff: "off",
    nightShiftWorking: (end: string) => `working until ${end}`,
    nightShiftOffHint: "Off: agents only work on todos when someone starts them. Agent admins turn it on under Todos.",
    nightShiftWindow: (start: string, end: string) => `Works ${start}–${end} (Berlin time) on the todos flagged for the night.`,
    nightShiftTonight: "Tonight so far",
    nightShiftLast: "Last night",
    nightShiftRuns: (n: number, usd: string) => `${n} run${n === 1 ? "" : "s"} · ${usd}`,
    nightShiftNothing: "No agent runs.",
    nightShiftReport: "Morning report",
    schorschi: "Schorschi",
    schorschiTalk: "Talk to Schorschi",
    schorschiPicture: "What he sees right now",
    schorschiQuiet: "All quiet. Nothing he watches needs attention.",
    quickActions: "Quick actions",
    quick: {
      ask: "Ask Smake",
      askHint: "A question in plain words, answered from the knowledge base",
      search: "Search knowledge",
      searchHint: "Systems, people, runbooks and documents",
      todo: "Add a todo",
      todoHint: "Your personal list, or hand it to an agent",
      action: "Run an action",
      actionHint: "Reviewed procedures: restarts, cache clears, deploys",
      tool: "Try a tool",
      toolHint: "Every tool you have, with a form",
      agent: "Start an agent",
      agentHint: "Let an agent investigate or fix something",
    },
    allTools: (n: number) => (n ? `All ${n} tools` : "All tools"),
    canChange: (n: number) => `${n} can change`,
    moreAreas: (n: number) => `${n} more area${n === 1 ? "" : "s"}`,
    getStartedAccountsNone: "Nothing to connect yet: no connection providers are set up.",
  },
  approvals: {
    title: "Approvals",
    description: "Changes that wait for a person to approve them. Nothing runs until someone does.",
    mine: "Mine",
    others: "Others' requests",
    empty: "Nothing is waiting for approval.",
    emptyHint: "When a change in Claude can't be confirmed in the chat, it lands here.",
    requester: "Requested by",
    tool: "Tool",
    target: "Target",
    risk: "Risk",
    reversible: "Can be undone",
    notReversible: "Cannot be undone",
    affected: (n: number) => `${n} item${n === 1 ? "" : "s"} affected`,
    approve: "Approve and run",
    decline: "Decline",
    declineConfirm: "Decline this change? It will not run.",
    typeToConfirm: (v: string) => `Type ${v} to confirm`,
    highRiskNote: "This is a high-risk change. Type the confirmation text exactly to enable approval.",
    approved: "Approved. The change is running.",
    fourEyes: "Needs a second person",
    fourEyesNote: "Four-eyes rule: someone other than the requester, with approval rights on this target, must approve before it runs. Approvers can also approve in Claude (approvals_read).",
    destructive: "Destructive",
    confirmOwn: "Confirm my request",
    confirmedOwn: "Confirmed. It now waits for a second person to approve.",
    waitingForSecond: "You confirmed this change. It now waits for a second person with approval rights on this target — ask a colleague to open this page.",
    waitingForRequester: (who: string) => `You approved. It runs as soon as ${who} confirms the request.`,
    approveAsSecond: "Approve as second person",
    approvedAsSecond: "Approved as second person.",
    undoOf: "This undoes an earlier change",
    requesterPending: (who: string) => `${who} has not confirmed the request yet; it runs once both of you agreed.`,
    breakGlass: "Approve alone (break-glass)",
    breakGlassHint: "Emergency only: approve your own change without a second person. Give a reason (at least 15 characters) — it is audited separately and reviewed.",
    breakGlassReason: "Reason for approving alone",
    breakGlassDone: "Approved with break-glass. The change is running.",
    approvedBy: "Approved by",
    confirmedByRequester: "Requester confirmed",
    breakGlassUsed: "Break-glass reason",
    aiReview: {
      title: "AI review",
      shadowBadge: "shadow",
      shadow: "Shadow mode: recorded only, it did not change how this change was handled.",
      verdict: { allow: "No concerns", ask: "Check this", escalate: "Second person advised", deny: "Refused", error: "Unavailable" },
      effect: {
        none: "The change took the normal path.",
        note: "Shown to the requester when confirming.",
        prompt: "The requester had to confirm with this concern in view.",
        auto_approve: "Ran without a prompt: the policy lets an AI \"allow\" stand in for the confirmation.",
        four_eyes: "The reviewer asked for a second person, so this change needs one.",
        refuse: "The reviewer refused it: it did not run from the chat, and only a second person's approval can let it through (when the policy allows).",
      },
    } as const,

    declined: "Declined. Nothing was changed.",
    expires: "Expires",
    expired: "This approval window has passed. Run the change again from Claude.",
    warnings: "Warnings",
    changes: "Changes",
    before: "Before",
    after: "After",
    detailsTitle: "Details",
    followJob: "Progress",
    status: {
      awaiting_approval: "Waiting for approval",
      executing: "Running",
      executed: "Done",
      declined: "Declined",
      expired: "Expired",
      failed: "Failed",
      undoing: "Undoing",
      undone: "Undone",
    } as Record<string, string>,
  },
  changes: {
    title: "Changes",
    description: "Every change made through Smake on your behalf, with undo where possible.",
    empty: "No changes yet.",
    undo: "Undo",
    undoConfirm: "Undo this change? The saved before-state will be restored.",
    undoStarted: "Undo started.",
    undoAwaitingSecond: "Undo requested. It runs once a second person with approval rights on this target approves it.",
  },
  tools: {
    title: "Tools",
    description: "Everything you can do through Claude, grouped by area. Try any tool right here.",
    searchPlaceholder: "Search tools…",
    empty: "No tools match.",
    read: "Read",
    write: "Change",
    targets: "Targets",
    tryIt: "Try it",
    targetCount: (n: number) => `${n} target${n === 1 ? "" : "s"}`,
    call: "Run tool",
    calling: "Running…",
    result: "Result",
    noResult: "Run the tool to see its result here.",
    approvalRequired: "This change needs approval before it runs.",
    writeNote: "This tool can change things. Changes that need confirmation stop at a preview here and wait for your approval.",
    openApproval: "Review and approve",
    planned: "Dry run: nothing was changed.",
    arguments: "Arguments",
    rawJson: "Raw JSON",
    form: "Form",
    runbooks: "Actions",
    steps: "Steps",
    prefilled: "Filled in from your call",
    prefillReview: "Masked secrets ([redacted]) and shortened values were not kept: fill them in again before you run it.",
    prefillOtherTool: "That call was made with another tool, so nothing was filled in.",
    prefillMissing: "That call is no longer in your activity (calls are kept 14 days).",
  },
  actions: {
    title: "Actions",
    description: "Reviewed, step-by-step procedures you can run on a system: check a server, restart workers, deploy a site. Pick where, then what.",
    byTarget: "By target",
    all: "All actions",
    history: "Run history",
    historyDescription: "Every run of an action you may see, newest first.",
    searchTargets: "Search targets…",
    searchActions: "Search actions…",
    chooseTarget: "Choose a target",
    chooseTargetHint: "Targets are a lineage's environments (e.g. strauss-eu:staging). Pick one to see what you can run there.",
    noTargets: "No actions are enabled for you yet",
    noTargetsHint: "Actions are reviewed procedures in the repository. Admins enable them per lineage; you also need the action's grant on the target.",
    noActionsHere: "Nothing to run on this target",
    actionsOn: (n: number, target: string) => `${n} action${n === 1 ? "" : "s"} on ${target}`,
    run: "Run",
    runOn: (target: string) => `Run on ${target}`,
    runAgain: "Run again",
    preview: "Preview (dry run)",
    details: "Details",
    readOnly: "Read-only",
    changes: "Makes changes",
    production: "Production",
    lastRun: "Last run",
    neverRun: "Not run here yet",
    by: (who: string | null) => `by ${who ?? "unknown"}`,
    steps: "Steps",
    step: (i: number) => `Step ${i}`,
    continueOnError: "Continues on error",
    stopsOnError: "Stops the run on error",
    capability: "Needs",
    enabledOn: "Enabled on",
    enabledOnHint: "Targets where you can run this action. Admins enable actions per lineage.",
    notEnabled: "Not enabled on any target you may use.",
    parameters: "Parameters",
    noParameters: "No parameters.",
    runIt: "Run it",
    target: "Target",
    targetPlaceholder: "Choose where to run it",
    fillIn: (fields: string) => `Please fill in: ${fields}`,
    runHistory: "Runs",
    noRuns: "No runs yet",
    noRunsHint: "Runs appear here once someone starts this action (dry runs are not recorded).",
    othersHidden: "You see your own runs. People who may approve changes see everyone's.",
    loadMore: "Load more",
    knowledge: "Knowledge",
    knowledgeHint: (tag: string) => `Tag a runbook page with “${tag}” to link it here.`,
    source: "Source",
    unavailable: "This action is not available to you",
    unavailableHint: "It is not enabled on any target you may act on, or it no longer exists.",
    runTitle: (title: string, target: string | null) => (target ? `${title} on ${target}` : title),
    awaitingApproval: "Waiting for approval",
    awaitingApprovalHint: "Nothing has run yet. Review the plan and approve it — then the steps start and this page follows them live.",
    reviewApprove: "Review and approve",
    declinedHint: "The run was declined; nothing was changed.",
    expiredHint: "The approval window passed; nothing was changed. Run it again to get a fresh plan.",
    planned: "Planned",
    perHost: "Per server",
    notApplicable: "Does not apply here",
    output: "Output",
    showOutput: "Show output",
    hideOutput: "Hide output",
    requester: "Started by",
    approver: "Approved by",
    started: "Started",
    duration: "Duration",
    confirmedVia: "Confirmed via",
    paramsUsed: "Parameters used",
    operation: "Change record",
    job: "Job",
    live: "Live",
    stepsDone: (done: number, total: number) => `${done} of ${total} steps`,
    state: {
      awaiting_approval: "Awaiting approval",
      running: "Running",
      succeeded: "Succeeded",
      completed_with_errors: "Completed with errors",
      failed: "Failed",
      declined: "Declined",
      expired: "Expired",
      pending: "Pending",
      skipped: "Skipped",
    } as Record<string, string>,
    linkFromKnowledge: "Run this action",
    mine: "Only mine",
  },
  apps: {
    title: "MCP Apps",
    description: "The interactive views Claude shows for tool results in claude.ai and Claude Desktop, rendered here with example data.",
    link: "App gallery",
    badge: "MCP App",
    viewApp: "App",
    viewData: "Text / JSON",
    usedBy: "Used by",
    noTools: "No tool uses this app yet.",
    fixture: "Example",
    noFixtures: "No example data for this app (apps/fixtures).",
    theme: "Theme",
    light: "Light",
    dark: "Dark",
    width: "Width",
    notBuilt: "This app is not built on this server: it shows a placeholder.",
    loadFailed: "Could not load the app.",
    wouldAsk: "In Claude, the app would ask:",
    empty: "No MCP apps on this server.",
  },
  jobs: {
    title: "Tasks & Jobs",
    description: "Long-running work: syncs, exports, agent runs and approved changes.",
    empty: "No jobs yet.",
    emptyHint: "Tools that take longer run in the background and show up here.",
    everyone: "Everyone's jobs",
    cancel: "Cancel job",
    cancelConfirm: "Cancel this job? Work already done is kept.",
    cancelled: "Job cancelled.",
    input: "Input",
    result: "Result",
    error: "Error",
    live: "Live",
  },
  connections: {
    title: "Connections",
    description: "Your personal accounts. Changes in GitHub, Jira, Miro or Nightwatch are always made as you.",
    connect: "Connect",
    reconnect: "Reconnect",
    disconnect: "Disconnect",
    disconnectConfirm: (label: string) => `Disconnect ${label}? Tools that need it will ask you to connect again.`,
    disconnected: "Disconnected.",
    empty: "No connection providers are set up yet.",
    status: {
      connected: "Connected",
      not_connected: "Not connected",
      needs_reauth: "Needs reconnecting",
      error: "Error",
      disabled: "Disabled by admin",
    } as Record<string, string>,
    lastUsed: "Last used",
    connectedAs: "Connected as",
    credentialMethod: "How to connect",
    credentialsStored: "Checked with the service right away and stored encrypted; they are never shown again. Disconnecting deletes them here — revoke them in the service too if you no longer need them.",
    checking: "Checking…",
  },
  activity: {
    title: "My activity",
    description: "What you did through Claude and this app.",
    empty: "No activity yet.",
    calls: "Calls",
    changes: "Changes",
    callsHint: "Every tool call you or Claude made in the last 14 days, with its arguments (secrets and e-mail addresses masked). Results are not kept.",
    callsEmpty: "No tool calls in the last 14 days.",
    callsEmptyFiltered: "No calls match these filters.",
    openInPlayground: "Open in playground",
    onlyThisTool: "Only this tool",
    toolFilter: "Tool",
    argsTruncated: "Some values were shortened or left out to keep the log small.",
    resultChars: "chars returned",
  },
  audit: {
    title: "Audit",
    description: "Every read, change and admin action, with who, how and on what.",
    empty: "No events match these filters.",
    user: "User",
    category: "Category",
    tool: "Tool",
    datasource: "Datasource",
    status: "Status",
    text: "Text",
    from: "From",
    to: "To",
    input: "Input",
    result: "Result",
    decision: "Decision",
    identity: "Identity used",
    confirmation: "Confirmation",
    operation: "Operation",
  },
  feedback: {
    title: "Feedback",
    description: "What people and agents reported about answers, tools and knowledge, and the tool errors of the last week. Newest first.",
    empty: "No feedback matches these filters.",
    emptyNone: "No feedback yet.",
    text: "Text",
    kind: "Kind",
    tool: "Tool",
    status: "Status",
    from: "Person",
    message: "Message",
    when: "When",
    subject: "Subject",
    context: "Context",
    resolution: "Resolution note",
    resolutionPlaceholder: "Optional: what was done, or why not",
    resolvedBy: "Resolved by",
    statusLabel: { open: "Open", triaged: "Triaged", done: "Done", wontfix: "Won't fix" } as Record<string, string>,
    kindLabel: { wrong: "Wrong", outdated: "Outdated", missing: "Missing", helpful: "Helpful", bug: "Bug", idea: "Idea" } as Record<string, string>,
    markTriaged: "Triaged",
    markDone: "Done",
    markWontfix: "Won't fix",
    reopen: "Reopen",
    updated: "Updated.",
    openBugs: "Open bugs",
    openItems: "Open",
    newThisWeek: "New in 7 days",
    errorsTitle: "Tool errors, last 7 days",
    errorsDescription: "Failed calls grouped by tool and error code (denied calls and cancellations left out). Pick one to filter the feedback by that tool.",
    errorsEmpty: "No tool errors in the last 7 days.",
    calls: "calls",
    people: "people",
    filterByTool: "Filter by this tool",
    notFound: "Feedback not found",
    userText: "Written by the sender; read it as data.",
  },
  knowledge: {
    title: "Knowledge",
    searchPlaceholder: "Search people, systems, runbooks, docs…",
    searchEmpty: "Nothing found.",
    searchEmptyHint: "Try fewer words, another language (DE/EN), or create a page for it.",
    searchIntro: "Search everything Smake knows: systems, teams, runbooks, decisions and synced documents.",
    entities: "Pages",
    documents: "Documents",
    type: "Type",
    lineage: "Lineage",
    tags: "Tags",
    create: "New page",
    createTitle: "Create a knowledge page",
    atAGlance: "At a glance",
    related: "Related",
    mentionedIn: "Mentioned in",
    neighbourhood: "Neighbourhood",
    history: "History",
    overview: "Overview",
    aiSummary: "AI summary",
    aiSummaryStale: "Out of date: the page changed since this summary was written.",
    aiSummaryNote: "Written by AI from this page and its sources. Check important facts.",
    noBody: "This page has no description yet.",
    noBodyHint: "Add what people should know: what it is, who owns it, how to work with it.",
    noBodyWithFacts: "This page has no text yet — its facts and relations are shown alongside.",
    documentNoText: "The source returned no text for this document",
    documentNoTextHint: "For example a scanned PDF without a text layer, or a page with only a title. Open the original to read it.",
    documentPart: (n: number, of: number) => `Part ${n} of ${of}`,
    maintenance: "Maintenance",
    maintenanceDescription: "Find duplicates, low-value entries, orphans and tag variants — and clean them up with a preview first. Every change can be reverted.",
    restore: "Restore",
    restoreTitle: (r: number) => `Restore revision ${r}?`,
    restoreBody: "The page returns to this revision. Nothing is lost: the current version stays in the history.",
    restored: "Page restored.",
    conflictTitle: "Someone else changed this page",
    conflictBody: "Your edit was based on an older revision. Review the newer version below, then save again or copy your text.",
    saved: "Page saved.",
    editReason: "What did you change?",
    origin: {
      human: "Written by a person",
      connector: "From a connected system",
      agent: "Added by an AI agent",
      extraction: "Extracted by AI from documents",
    } as Record<string, string>,
    conflict: "Conflicting sources",
    inbox: "Inbox",
    inboxDescription: "Suggestions, conflicts and possible duplicates waiting for a decision. Each has a verdict with a reason: decide whole groups after a preview, or item by item.",
    inboxEmpty: "The inbox is empty. Nice.",
    accept: "Accept",
    reject: "Reject",
    changeLog: "Change log",
    changeLogDescription: "Every change to the knowledge base. Revert a whole change set if something went wrong.",
    changeLogEmpty: "No changes match.",
    revert: "Revert",
    revertTitle: "Revert this change set?",
    views: "Views",
    sources: "Sources",
    sourcesDescription: "Systems synced into the knowledge base.",
    sourcesEmpty: "No sources yet. Add one to start syncing documents.",
    syncNow: "Sync now",
    syncManual: "Synced by hand",
    readableBy: (who: string) => `Readable by ${who}`,
    readableByEveryone: "Readable by everyone",
    aiSuggestionsOn: "AI suggestions on",
    runs: "Runs",
    openOriginal: "Open original",
    document: "Document",
    ask: "Ask",
    askTitle: "Ask Smake",
    askDescription: "Ask a question in plain language. Answers come from the knowledge base, with sources.",
    askPlaceholder: "e.g. Who owns the checkout service? / Wer betreut die Produktion in Hamburg?",
    askButton: "Ask",
    askExamples: ["Who owns the checkout service?", "Wie starte ich die Queue-Worker auf Staging neu?", "What breaks if orders-db is down?"],
    sources_: "Sources",
    // Overview (landing)
    overviewIntro: "Everything Smake knows about its systems, teams and how things work — and what needs care.",
    searchShortcut: "Press / to search",
    instantEmpty: "No quick matches. Press Enter to search everything.",
    seeAllResults: (q: string) => `See all results for “${q}”`,
    lineageMap: "Lineages",
    lineageMapHint: "Each product line with its environments and what runs there.",
    lineageMapEmpty: "No lineages yet. They appear once systems are synced or pages name their lineage.",
    browseByType: "Browse by type",
    tagIndex: "Tags",
    tagIndexEmpty: "No tags yet.",
    recentlyChanged: "Recently changed",
    recentlyChangedEmpty: "Nothing changed recently.",
    mine: "Mine",
    mineOwned: "Owned by you or your team",
    mineEdited: "Edited by you (90 days)",
    mineEmpty: "Pages you edit or own show up here.",
    mineNoPerson: "Tip: a person page with your username links the pages you own.",
    needsAttention: "Needs attention",
    needsAttentionHint: "Gaps a writer can close in a few minutes.",
    allGood: "Nothing needs attention right now.",
    inboxWaiting: "Inbox",
    inboxNoise: (n: string) => `Start with ${n} likely noise, rejected in groups`,
    conflicts: "Conflicts between sources",
    missingOwner: "Pages without an owner",
    stalePages: (days: number) => `Not reviewed for ${days}+ days`,
    zeroResults: "Searched for, not found (30 days)",
    zeroResultsHint: "Create the page people were looking for.",
    createPageFor: (q: string) => `Create page “${q}”`,
    // Results
    results: (n: number, formatted: string) => `${formatted} ${n === 1 ? "result" : "results"}`,
    facetsNote: "Counts cover all results.",
    filters: "Filters",
    status: "Status",
    source: "Source",
    didYouMean: "Did you mean",
    resolvedRef: "This reference points to",
    askInstead: "Ask instead",
    // Lineage page
    lineageTopology: "Topology",
    lineageRecent: "Recent changes here",
    lineageOwners: "Teams and owners",
    lineageDocuments: "Documents mentioning it",
    lineageEmptyType: "None recorded.",
    // Entity page
    owner: "Owner",
    noOwner: "No owner recorded",
    factFrom: (title: string) => `from ${title}`,
    staleWarning: (days: number) => `Not reviewed or edited for ${days} days. Check that it is still right.`,
  },
  admin: {
    title: "Administration",
    roles: "Roles",
    grants: "Grants",
    bindings: "Who has this role",
    addGrant: "Add grant",
    addBinding: "Add people",
    newRole: "New role",
    simulator: "Can X do Y?",
    simulatorDescription: "Check what a person may do before they hit a wall.",
    principals: "People",
    breakGlass: "Break-glass",
    breakGlassDescription: "Temporarily elevate yourself for an emergency. Everything is audited.",
    datasources: "Datasources",
    datasourcesDescription: "Connections to databases, clusters and services. Secrets are write-only.",
    newDatasource: "New datasource",
    testConnection: "Test connection",
    secretKeep: "Leave as is to keep the stored secret.",
    importServers: "Import servers as SSH hosts",
    importServersDescription:
      "Each server gets its own SSH key, installed for the forge user through Forge. The server's host key is read through Forge's API and must match what the server presents, then it is pinned.",
    importServersApply: "Import selected",
    importServersEmpty: "No servers in this Forge organization.",
    importUnmappedTarget: "Target for unmapped servers",
    importAllowTofu: "Allow trust on first use (insecure)",
    importAllowTofuHint:
      "Only used when Forge cannot provide a server's host keys (the token lacks recipe and site command permissions): the key the server presents is pinned without verification. Recorded in the audit log.",
    rotateKey: "Rotate key",
    rotateKeyConfirm: "A new key is installed through Forge and checked against the pinned host key; then the old key is removed from the server.",
    settings: "Settings",
    settingsDescription: "Runtime settings. Changes apply right away and are recorded in the audit log.",
    locked: "Locked",
    settingsPage: {
      search: "Search settings…",
      categories: {
        ai: "AI & budgets",
        knowledge: "Knowledge",
        operations: "Operations",
        mcpApps: "MCP apps",
        designer: "Designer",
        media: "Media",
        schorschi: "Schorschi",
      } as Record<string, string>,
      modified: "Modified",
      invalid: "Invalid stored value",
      invalidHint: "The stored value does not match the setting's schema, so the default applies. Save a valid value or reset it.",
      default: "Default",
      unit: (u: string) => `in ${u}`,
      lockedHint: (env: string) => `Pinned by the server environment (${env} in SETTINGS_LOCKED). Change it in the deployment.`,
      restartRequired: "Takes effect after a restart",
      reset: "Reset to default",
      resetConfirm: (key: string) => `Reset ${key} to its default?`,
      saved: (title: string) => `${title} saved.`,
      resetDone: (title: string) => `${title} is back to its default.`,
      changedBy: (who: string) => `Changed by ${who}`,
      editedElsewhere: "Edited on its own page",
      storedKeys: (n: number) => `${n} stored`,
      unknownKeys: (n: number) => `Unknown keys (${n})`,
      unknownHint: "Stored values no setting uses any more (renamed or removed). They have no effect; delete them when you are sure.",
      deleteKey: "Delete",
      deleteConfirm: (key: string) => `Delete the stored value ${key}? It is not used by any setting.`,
      deleted: (key: string) => `${key} deleted.`,
      empty: "No settings match.",
      editAsJson: "Edit as JSON",
      editAsForm: "Edit as form",
      fixJsonFirst: "Fix the JSON before switching back to the form.",
      compare: "Compare with default",
      current: "Current",
      unsaved: "Unsaved",
      edit: "Edit",
      view: "View",
      close: "Close",
      editing: "Edit the value",
      lockedValue: "Current value (read-only)",
      on: "On",
      off: "Off",
      notSet: "Not set",
      emptyList: "Empty list",
      noEntries: "No entries",
      items: (n: number) => `${n} ${n === 1 ? "item" : "items"}`,
      entries: (n: number) => `${n} ${n === 1 ? "entry" : "entries"}`,
      categoriesLabel: "Categories",
      modifiedOnly: "Modified",
      expandAll: "Expand all",
      collapseAll: "Collapse all",
      count: (n: number) => `${n} ${n === 1 ? "setting" : "settings"}`,
      modifiedCount: (n: number) => `${n} modified`,
      unsavedCount: (n: number) => `${n} unsaved`,
      unsavedHint: (n: number) => `${n} ${n === 1 ? "setting has" : "settings have"} unsaved changes. Save each one in its row.`,
      results: (n: number) => `${n} ${n === 1 ? "match" : "matches"}`,
    },
    users: "Users",
    usersDescription: "People in Keycloak and their Smake access.",
    disableUser: "Disable",
    enableUser: "Enable",
    connectionProviders: "Connection providers",
    connectionProvidersDescription: "OAuth apps people use to connect their own accounts.",
    mcpClientsDescription: "MCP apps that registered with Smake's sign-in; approve waiting ones, block bad ones.",
    mcpClientsAudited: "Approving and blocking are audited.",
    ai: "AI",
    aiDescription: "Providers, which model does which task, what it costs and who may spend how much.",
    aiTabs: { routes: "Routing", rules: "Rules", providers: "Providers", gateway: "Gateway", budgets: "Budgets", usage: "Usage & cost" } as Record<string, string>,
    realtimeRouteHint: "A live voice call uses only the provider and model: reasoning effort and token caps do not apply. The model needs a price (built in or the setting ai.pricing), otherwise calls are refused.",
    realtimeTask: "Live speech-to-speech calls (speech in, speech out).",
    realtimeFallbacks: "Fallbacks (the first usable one starts the call; a running call never switches)",
    realtimeTest: "Checks that the provider accepts the model; no call is placed.",
    budgets: "Budgets",
    budgetsDescription:
      "Everyone is subject to every policy that applies to them (global, each of their roles, personal); the tightest wins. A personal override replaces the global and role limits of its period. Periods are UTC calendar days, weeks (from Monday) and months.",
    newBudget: "New policy",
    budgetDefaults: "Defaults from settings",
    budgetDefaultsHint:
      "These apply until you create an enabled global policy of the same kind (per person / pool). The AI gateway budgets (ai.budget.gateway_*) always apply, to gateway spend only.",
    topups: "Top-ups",
    topupsDescription: "Temporary extra allowance for one person: added to every per-person limit until it expires.",
    newTopup: "Grant top-up",
    effectiveBudget: "Check someone's budget",
    runbooks: "Actions",
    runbooksDescription: "Actions are reviewed YAML files in the repository. Here you enable them per lineage or target and set parameter defaults.",
    system: "System",
    systemDescription: "All jobs, everyone who used Smake, and versions.",
  },
  knowledgeUpload: {
    button: "Upload",
    title: "Upload to the knowledge base",
    description: "PDF, Markdown, text, HTML, CSV or an image, up to 300 MB. Pictures and charts are kept and captioned; scanned pages are read with OCR.",
    choose: "Choose a file",
    uploading: "Uploading…",
    tooLarge: "That file is larger than 300 MB.",
    phases: { upload: "Upload", text: "Text", images: "Images", ocr: "Scanned pages", captions: "Captions" },
    converting: "Converting in the background. You can close this dialog; the job keeps running.",
    enriching: "The document is ready. Captions and text recognition are running in the background.",
    done: "Done",
    failed: "The conversion failed.",
    openDocument: "Open document",
    openJob: "Show job",
    summaryPages: "pages",
    summaryImages: "images kept",
    summarySkipped: "skipped (small, blank, repeated)",
    summaryCaptions: "captions",
    summaryOcr: "scanned pages read",
    summaryCost: "AI cost",
    summaryWaiting: "waiting for AI",
    pendingBanner: "Some images of this document have no caption yet, or scanned pages were not read.",
    runNow: "Caption and read now",
    queued: "Queued. Captions appear here when the job is done.",
  },
} as const;

export type Strings = typeof t;
